export class HeroVideoPlayer extends HTMLElement {
  #observer = null;
  #resizeHandler = null;

  connectedCallback() {
    const video = this.querySelector('video');
    if (!video) return;

    // Ensure audio is muted and playsinline is set for browser autoplay policies
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');

    const isVisibleInLayout = () => {
      return this.offsetParent !== null || window.getComputedStyle(this).display !== 'none';
    };

    const isSlideActive = () => {
      const slide = this.closest('slideshow-slide');
      if (!slide) return true;
      const siblings = slide.parentElement?.children;
      if (!siblings || siblings.length <= 1) return true;
      return slide.getAttribute('aria-hidden') === 'false';
    };

    const playVideo = () => {
      if (isVisibleInLayout() && isSlideActive()) {
        video.muted = true;
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {
            // Autoplay may be queued or deferred until next user interaction
          });
        }
      }
    };

    const pauseVideo = () => {
      if (!video.paused) {
        video.pause();
      }
    };

    // Attempt playback immediately and when data buffers
    playVideo();
    video.addEventListener('loadedmetadata', playVideo, { once: true });
    video.addEventListener('loadeddata', playVideo, { once: true });
    video.addEventListener('canplay', playVideo, { once: true });

    // Monitor slide transitions in slideshow (aria-hidden attribute changes)
    const slide = this.closest('slideshow-slide');
    if (slide) {
      this.#observer = new MutationObserver((mutations) => {
        for (const m of mutations) {
          if (m.type === 'attributes' && m.attributeName === 'aria-hidden') {
            if (slide.getAttribute('aria-hidden') === 'false') {
              playVideo();
            } else {
              pauseVideo();
            }
          }
        }
      });
      this.#observer.observe(slide, { attributes: true, attributeFilter: ['aria-hidden'] });
    }

    // Handle responsive breakpoint changes between mobile (9:16) and desktop (16:9)
    this.#resizeHandler = () => {
      if (isVisibleInLayout() && isSlideActive()) {
        playVideo();
      } else {
        pauseVideo();
      }
    };
    window.addEventListener('resize', this.#resizeHandler, { passive: true });

    // Handle theme editor events
    document.addEventListener('shopify:block:select', this.#resizeHandler);
    document.addEventListener('shopify:section:load', this.#resizeHandler);
  }

  disconnectedCallback() {
    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }
    if (this.#resizeHandler) {
      window.removeEventListener('resize', this.#resizeHandler);
      document.removeEventListener('shopify:block:select', this.#resizeHandler);
      document.removeEventListener('shopify:section:load', this.#resizeHandler);
      this.#resizeHandler = null;
    }
  }
}

if (!customElements.get('hero-video-player')) {
  customElements.define('hero-video-player', HeroVideoPlayer);
}

// User-gesture fallback for browsers with strict low-power / battery saver autoplay constraints
const triggerHeroVideosPlayback = () => {
  document.querySelectorAll('hero-video-player video').forEach((vid) => {
    const player = vid.closest('hero-video-player');
    if (player && (player.offsetParent !== null || window.getComputedStyle(player).display !== 'none')) {
      const slide = player.closest('slideshow-slide');
      if (!slide || slide.getAttribute('aria-hidden') === 'false') {
        vid.muted = true;
        vid.play().catch(() => {});
      }
    }
  });
};

window.addEventListener('click', triggerHeroVideosPlayback, { once: true, passive: true });
window.addEventListener('touchstart', triggerHeroVideosPlayback, { once: true, passive: true });
window.addEventListener('scroll', triggerHeroVideosPlayback, { once: true, passive: true });
